import { useState } from 'react';

import { Avatar, Box, Divider, Flex, NavLink, Paper, Stack, Text } from '@mantine/core';
import {
  IconExternalLink,
  IconFlag,
  IconLogout,
  IconMail,
  IconNotes,
  IconPrinter,
  IconSettingsCog,
  IconUser
} from '@tabler/icons-react';

import { useAppSelector } from '@/store/hooks';
import { useFeatureFlag } from '@/store/slices/api/featureFlagsApiSlice';
import { useAuth } from '@/hooks/useAuth';
import FallbackText from '@/components/FallbackText';

import { UserRole } from '@/types/user';

import { RULE_BASED_TEMPLATES_FLAG } from '../physician/TransmissionReportPage/ruleBasedTemplates';

import styles from './ApplicationSettingsPage.module.scss';
import FeatureFlagPage from './FeatureFlags';
import InterpretationTemplatesSection from './InterpretationTemplatesSection';
import { canManageLetterTemplates } from './letterTemplates';
import LetterTemplatesSection from './LetterTemplatesSection';
import PrintLettersSection from './PrintLettersSection';

const ICON = 18;
const STROKE = 1.6;
const OPS_PORTAL_URL = import.meta.env.VITE_OPS_APP_URL ?? '#';

type SettingsSection =
  | 'profile'
  | 'flags'
  | 'letters'
  | 'print-letters'
  | 'my-templates'
  | 'clinic-templates';

const ApplicationSettingsPage = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { logout } = useAuth();
  const [section, setSection] = useState<SettingsSection>('profile');
  const name = `${user?.given ?? ''} ${user?.family ?? ''}`.trim();
  const role = user?.role?.split(':').slice(-1)[0] ?? '—';
  const userRole = user?.role as UserRole | undefined;
  const isSuperAdmin = userRole === UserRole.InternalSuperAdmin;
  const showOpsPortal =
    isSuperAdmin ||
    userRole === UserRole.InternalAdmin ||
    (user?.role ?? '').toLowerCase().includes('supervisor');
  const showLetterTemplates = canManageLetterTemplates(userRole);
  const showInterpretationTemplates = useFeatureFlag(RULE_BASED_TEMPLATES_FLAG);

  const handleSignOut = () => {
    sessionStorage.setItem('skipReviewBeforeUnload', '1');
    void logout();
  };

  const field = (label: string, value?: string | null) => (
    <Flex justify="space-between" gap="md">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={500}>
        {value || '—'}
      </Text>
    </Flex>
  );

  return (
    <div className={styles.layout}>
      <Box pb="lg">
        <Text className={styles.title}>Settings</Text>
      </Box>

      <Flex gap={24} align="flex-start">
        <Paper withBorder radius="md" p="xs" w={240} style={{ flexShrink: 0 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" pt={4} pb={2}>
              Account
            </Text>
            <NavLink
              label="Profile"
              leftSection={<IconUser size={ICON} stroke={STROKE} />}
              active={section === 'profile'}
              onClick={() => setSection('profile')}
              styles={{ root: { borderRadius: 8 } }}
            />
            {showInterpretationTemplates && (
              <NavLink
                label="My Templates"
                leftSection={<IconNotes size={ICON} stroke={STROKE} />}
                active={section === 'my-templates'}
                onClick={() => setSection('my-templates')}
                styles={{ root: { borderRadius: 8 } }}
              />
            )}
            {showLetterTemplates && (
              <>
                <Divider my={6} />
                <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" pt={4} pb={2}>
                  Clinic
                </Text>
                <NavLink
                  label="Letter templates"
                  leftSection={<IconMail size={ICON} stroke={STROKE} />}
                  active={section === 'letters'}
                  onClick={() => setSection('letters')}
                  styles={{ root: { borderRadius: 8 } }}
                />
                <NavLink
                  label="Print letters"
                  leftSection={<IconPrinter size={ICON} stroke={STROKE} />}
                  active={section === 'print-letters'}
                  onClick={() => setSection('print-letters')}
                  styles={{ root: { borderRadius: 8 } }}
                />
                {showInterpretationTemplates && (
                  <NavLink
                    label="Interpretation templates"
                    leftSection={<IconNotes size={ICON} stroke={STROKE} />}
                    active={section === 'clinic-templates'}
                    onClick={() => setSection('clinic-templates')}
                    styles={{ root: { borderRadius: 8 } }}
                  />
                )}
              </>
            )}
            {(isSuperAdmin || showOpsPortal) && (
              <>
                <Divider my={6} />
                <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" pt={4} pb={2}>
                  Platform
                </Text>
                {isSuperAdmin && (
                  <NavLink
                    label="Feature Flags"
                    leftSection={<IconFlag size={ICON} stroke={STROKE} />}
                    active={section === 'flags'}
                    onClick={() => setSection('flags')}
                    styles={{ root: { borderRadius: 8 } }}
                  />
                )}
                {showOpsPortal && (
                  <NavLink
                    label="Operations Portal"
                    leftSection={<IconSettingsCog size={ICON} stroke={STROKE} />}
                    rightSection={<IconExternalLink size={14} stroke={STROKE} />}
                    component="a"
                    href={OPS_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    styles={{ root: { borderRadius: 8 } }}
                  />
                )}
              </>
            )}
            <Divider my={6} />
            <NavLink
              label="Sign out"
              leftSection={<IconLogout size={ICON} stroke={STROKE} />}
              onClick={handleSignOut}
              color="red"
              styles={{ root: { borderRadius: 8, color: 'var(--mantine-color-red-7)' } }}
            />
          </Stack>
        </Paper>

        <Box style={{ flex: 1, minWidth: 0 }}>
          {section === 'my-templates' && showInterpretationTemplates ? (
            <InterpretationTemplatesSection mode="mine" />
          ) : section === 'clinic-templates' &&
            showInterpretationTemplates &&
            showLetterTemplates ? (
            <InterpretationTemplatesSection mode="clinic" />
          ) : section === 'letters' && showLetterTemplates ? (
            <LetterTemplatesSection />
          ) : section === 'print-letters' && showLetterTemplates ? (
            <PrintLettersSection />
          ) : section === 'flags' && isSuperAdmin ? (
            <FeatureFlagPage />
          ) : (
            <Box>
              <Text fw={700} size="lg" mb={4}>
                Profile
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                Your account details.
              </Text>
              <Paper p="lg" withBorder maw={460}>
                <Flex align="center" gap="md" mb="md">
                  <Avatar
                    color="initials"
                    allowedInitialsColors={['plum']}
                    name={name}
                    variant="filled"
                    radius="xl"
                    size="lg"
                    styles={{ placeholder: { fontSize: '1rem', fontWeight: 700 } }}
                  />
                  <FallbackText size="xl" c="neutral.8" fw={700}>
                    {name}
                  </FallbackText>
                </Flex>
                <Stack gap="sm">
                  {field('Email', user?.email)}
                  {field('Role', role)}
                </Stack>
              </Paper>
            </Box>
          )}
        </Box>
      </Flex>
    </div>
  );
};

export default ApplicationSettingsPage;
